/**
 * CUSTOM: injects localizable HTML text overlays onto product gallery
 * images, replacing text that used to be baked into the alt images
 * themselves. Content comes from product.metafields.hl_.gallery_overlays
 * (see snippets/hl-gallery-overlay.liquid and docs/gallery-overlay-content.md).
 *
 * This theme's actual product templates (product.hair-luxious-tablets,
 * -shampoo-conditioner, -set — i.e. every live product) render their gallery
 * via sections/pdp-buybox.liquid's own net-new <hl-gallery> component, NOT
 * Horizon's stock <media-gallery>/<slideshow-slide> markup. <hl-gallery> is
 * a single horizontally snap-scrolling track (.hl-gallery__slide per media
 * item, see assets/pdp-buybox.js's HlGallery class) with no separate
 * desktop/grid layout to fight with, so overlays just target that directly.
 * Horizon's stock <media-gallery> is still supported as a fallback (used by
 * templates/product.json, which isn't assigned to any live product today)
 * in case a future template goes back to the stock section.
 *
 * Slides are matched to overlay entries via each slide's data-index
 * attribute (added in pdp-buybox.liquid, or via the --slide-N custom
 * property Horizon sets on <slideshow-slide>), not raw DOM position, since
 * order can shift when a variant's featured media is promoted to the front.
 *
 * v1 targets the first gallery element on the page — correct for a
 * standard PDP with a single gallery. If a template ever renders more than
 * one product's gallery on the same page, this will need a way to scope
 * each <hl-gallery-overlay> to its own gallery.
 *
 * UPSTREAM risk: LOW — net-new file, only touches elements it creates itself.
 */

class HlGalleryOverlay extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hlInitialized) return;
    this.dataset.hlInitialized = 'true';

    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;

    let overlays;
    try {
      overlays = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(overlays) || overlays.length === 0) return;

    overlays = this.dropSubscriptionIfUnavailable(overlays);
    if (overlays.length === 0) return;

    const slideTargets = this.findSlideTargets();
    if (!slideTargets || slideTargets.size === 0) return;

    overlays.forEach((overlay) => {
      const zeroBasedIndex = (overlay.imageIndex || 0) - 1;
      const target = slideTargets.get(zeroBasedIndex);
      if (!target) return;
      const el = this.buildSlide(overlay);
      if (el) target.appendChild(el);
    });

    this.labelThumbs(overlays);
  }

  /**
   * Second half of the subscription-slide gate. The snippet already drops the
   * slide when the variant has no selling plan allocations at all; this covers
   * the other way subscriptions get switched off — plans exist, but the buy box
   * is told not to offer them via the hl-pack-picker block's own
   * `enable_subscriptions` setting.
   *
   * A block's settings can't be read from another file, so Liquid can't see
   * that. The picker does publish its resolved answer as data-has-plans, so
   * this reads that rather than re-deriving the decision — same reasoning as
   * reading .hl-gallery__thumb--active for the active slide: the component that
   * owns the question keeps owning it.
   *
   * Only acts when the attribute is present AND false. If the pack picker isn't
   * on the template, this does nothing and the snippet's allocation check
   * stands on its own.
   *
   * In practice this gate, not the snippet's, is the one that fires. Measured
   * on live For Her Tablets during the single-purchase-option test on
   * 2026-08-07: selling_plan_groups 1, variant allocations 9, no Subscribe card
   * rendered, data-has-plans "false". Seal keeps its plans attached while the
   * store sells single purchases only, so an allocations check sees a
   * subscribable product and the buy box disagrees. Don't remove this on the
   * assumption the Liquid side covers it.
   *
   * Filtering here rather than hiding with CSS means the thumbnail caption goes
   * too — a "Subscribe" label pointing at a slide with no overlay would be
   * worse than either.
   */
  dropSubscriptionIfUnavailable(overlays) {
    const picker = document.querySelector('[data-has-plans]');
    if (!picker || picker.dataset.hasPlans !== 'false') return overlays;
    return overlays.filter((o) => o.slideType !== 'subscription');
  }

  /**
   * Captions EVERY thumbnail, turning the strip into a contents list.
   *
   * The first version of this printed one caption under the strip for whatever
   * slide was in view. That names what you're already looking at, which is the
   * one thing you don't need — it gave no reason to look at slide 5. Seven
   * small, near-identical product squares don't advertise that one of them is
   * the ingredients and another is a customer review, so the labels have to be
   * visible all at once for the strip to work as an invitation to browse.
   *
   * Each thumb is wrapped in a column with its name underneath. Wrapping is
   * done here rather than in sections/pdp-buybox.liquid because the names come
   * from the gallery_overlay metaobjects, which that section doesn't read — it
   * renders media_list. Keeping the DOM surgery in one place also means the
   * gallery markup is unchanged for products with no overlays.
   *
   * Copy comes from the JSON payload, which gets it from the `hl.gallery_slide`
   * locale block — this file deliberately holds no user-facing strings, same
   * convention as assets/hl-pack-picker.js.
   *
   * Active state is read rather than computed: pdp-buybox.js's HlGallery
   * already owns which thumb is current (it toggles .hl-gallery__thumb--active
   * from an IntersectionObserver on the slides), and the active caption is
   * emphasised off that class with :has() in CSS. Nothing in pdp-buybox.js has
   * to change, and no second notion of "current slide" exists to drift.
   */
  labelThumbs(overlays) {
    const gallery = document.querySelector('hl-gallery');
    if (!gallery) return;

    const thumbsEl = gallery.querySelector('[data-gallery-thumbs]');
    if (!thumbsEl) return;

    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    if (thumbs.length === 0) return;

    const labels = new Map();
    overlays.forEach((o) => {
      if (o.label) labels.set((o.imageIndex || 0) - 1, o.label);
    });
    if (labels.size === 0) return;

    thumbsEl.classList.add('hl-gallery__thumbs--labelled');

    thumbs.forEach((thumb) => {
      const label = labels.get(Number(thumb.dataset.index));
      if (!label) return;

      // Append rather than replace: 'hl.thumbnail' already gives screen
      // readers the position ("Show image 3"), which is still worth having.
      const existing = thumb.getAttribute('aria-label');
      thumb.setAttribute('aria-label', existing ? `${existing} — ${label}` : label);

      const caption = document.createElement('span');
      caption.className = 'hl-gallery__thumb-label';
      // aria-hidden: the button's aria-label already carries this text, and
      // without it a screen reader reads every name twice.
      caption.setAttribute('aria-hidden', 'true');
      caption.textContent = label;
      thumb.appendChild(caption);
    });
  }

  findSlideTargets() {
    const hlGallery = document.querySelector('hl-gallery');
    if (hlGallery) {
      const targets = new Map();
      hlGallery.querySelectorAll('[data-gallery-slide]').forEach((slide, i) => {
        const index = slide.hasAttribute('data-index') ? Number(slide.getAttribute('data-index')) : i;
        targets.set(index, slide);
      });
      return targets;
    }

    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      const targets = new Map();
      mediaGallery.querySelectorAll('slideshow-slide').forEach((slide) => {
        const match = (slide.getAttribute('style') || '').match(/--slide-(\d+)/);
        if (!match) return;
        const media = slide.querySelector('.product-media');
        if (media) targets.set(Number(match[1]), media);
      });
      return targets;
    }

    return null;
  }

  /**
   * Green-bullet list, shared by the benefits, symptoms and trust slides.
   * Items are {heading, body}; body is optional, which is what lets the chip
   * slides reuse this with nothing but a phrase per row.
   */
  buildBulletList(items) {
    const list = document.createElement('div');
    list.className = 'hl-gallery-overlay-slide__list';
    items.forEach((item) => {
      if (!item || (!item.heading && !item.body)) return;
      const row = document.createElement('div');
      row.className = 'hl-gallery-overlay-slide__item';
      const dot = document.createElement('span');
      dot.className = 'hl-gallery-overlay-slide__item-dot';
      row.appendChild(dot);
      const textWrap = document.createElement('div');
      if (item.heading) {
        textWrap.appendChild(this.el('div', 'hl-gallery-overlay-slide__item-heading', item.heading));
      }
      if (item.body) {
        textWrap.appendChild(this.el('div', 'hl-gallery-overlay-slide__item-body', item.body));
      }
      row.appendChild(textWrap);
      list.appendChild(row);
    });
    return list;
  }

  /**
   * Points the formula slide at the ingredient-transparency section below.
   *
   * The slide only ever shows four actives, so on its own it reads as though
   * that is the whole formula — on For Her Tablets it literally says "25 active
   * ingredients" above a list of four. This links to the full declaration.
   *
   * Rendered only when that section is actually on the page, so a template
   * without it gets no dead link. The section's id is generated per section
   * (HlIngTrans-{{ section.id }}), so it's found by class at runtime rather
   * than hardcoded here.
   *
   * A plain anchor, with no click handler at all. The first version called
   * preventDefault() and scrollIntoView({behavior:'smooth'}), which is strictly
   * worse: it takes over the browser's own behaviour and then has no fallback
   * if that call doesn't land, and it breaks middle-click, cmd-click and
   * "copy link address". The theme already sets scroll-behavior: smooth on
   * <html>, so a bare href gets the smooth scroll for free — and it still works
   * if this script ever fails to run.
   *
   * It deliberately doesn't activate a particular tab either. The obvious
   * candidate was "formula", but that panel is "Rest of Formula" — the
   * non-claim ingredients — so forcing it would land someone on a partial
   * list. The section's default tab is Active, a superset of the four the
   * slide just showed, and the other tabs are right there.
   */
  appendIngredientsHint(content) {
    const label = this.dataset.fullIngredientsLabel;
    if (!label) return;

    const section = document.querySelector('.hl-ingtrans');
    if (!section || !section.id) return;

    const link = document.createElement('a');
    link.className = 'hl-gallery-overlay-slide__more';
    link.href = `#${section.id}`;
    link.textContent = label;

    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '↓';
    link.appendChild(arrow);

    content.appendChild(link);
  }

  buildSlide(overlay) {
    const wrap = document.createElement('div');
    wrap.className = 'hl-gallery-overlay-slide';
    wrap.dataset.slideType = overlay.slideType || '';

    const content = document.createElement('div');
    content.className = 'hl-gallery-overlay-slide__content';
    wrap.appendChild(content);

    if (overlay.eyebrow) {
      content.appendChild(this.el('div', 'hl-gallery-overlay-slide__eyebrow', overlay.eyebrow));
    }
    if (overlay.heading) {
      content.appendChild(this.el('p', 'hl-gallery-overlay-slide__heading', overlay.heading));
    }
    if (overlay.subheading) {
      content.appendChild(this.el('p', 'hl-gallery-overlay-slide__subheading', overlay.subheading));
    }

    switch (overlay.slideType) {
      // Chips used to render as outlined pills. They were designed against the
      // old dark scrim, where a bordered pill was the only way to give a short
      // phrase any edge. On the frosted card the card itself is the surface,
      // so the pills became boxes drawn inside a box — and the symptoms and
      // trust slides ended up looking like a different component from the
      // benefits slide sitting two thumbnails away. They now use the same
      // green-bullet list, minus the body line each chip doesn't have.
      case 'symptoms':
      case 'trust':
      case 'subscription':
        if (Array.isArray(overlay.chips) && overlay.chips.length) {
          // `body`, not `heading`. A chip is a statement ("Less fullness"),
          // not the title of anything, so it takes the list's body treatment —
          // the same Poppins 300 the benefits rows use for their copy. Sending
          // it through `heading` gave these rows the step-title style and made
          // the two slides look like different components again.
          content.appendChild(
            this.buildBulletList(overlay.chips.map((chip) => ({ body: chip })))
          );
        }
        break;

      case 'benefits':
        if (Array.isArray(overlay.benefits) && overlay.benefits.length) {
          content.appendChild(this.buildBulletList(overlay.benefits));
        }
        break;

      case 'formula':
        if (Array.isArray(overlay.formula) && overlay.formula.length) {
          const grid = document.createElement('div');
          grid.className = 'hl-gallery-overlay-slide__formula';
          overlay.formula.forEach((i) => {
            const box = document.createElement('div');
            box.className = 'hl-gallery-overlay-slide__formula-box';
            // Name and dose only — the payload no longer carries a claim at
            // all. See the comment in snippets/hl-gallery-overlay.liquid for
            // why; the claims still render in the ingredient-transparency
            // section the slide links to.
            const nameLine = [i.name, i.amount].filter(Boolean).join(' — ');
            if (nameLine) box.appendChild(this.el('div', 'hl-gallery-overlay-slide__formula-name', nameLine));
            grid.appendChild(box);
          });
          content.appendChild(grid);
        }
        this.appendIngredientsHint(content);
        break;

      case 'review':
        if (overlay.rating) {
          content.appendChild(this.el('div', 'hl-gallery-overlay-slide__stars', '★'.repeat(overlay.rating)));
        }
        if (overlay.quote) {
          content.appendChild(this.el('p', 'hl-gallery-overlay-slide__quote', `“${overlay.quote}”`));
        }
        if (overlay.quoteAuthor) {
          content.appendChild(this.el('div', 'hl-gallery-overlay-slide__author', `— ${overlay.quoteAuthor}`));
        }
        break;

      default:
        break;
    }

    return wrap;
  }

  el(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    node.textContent = text;
    return node;
  }
}

if (!customElements.get('hl-gallery-overlay')) {
  customElements.define('hl-gallery-overlay', HlGalleryOverlay);
}
